import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export interface FileConversionResult {
  file: File;
  fileName: string;
  fileSize: number;
  fileType: string;
  base64Full: string;      // data:mime/type;base64,content
  base64Content: string;   // solo el contenido base64
}

export interface FileValidationConfig {
  maxSizeInMB?: number;
  allowedTypes?: string[];
  allowedExtensions?: string[];
  maxFilenameLength?: number;
}

@Injectable({
  providedIn: 'root'
})
export class FileHandlerService {

  private readonly defaultValidationConfig: FileValidationConfig = {
    maxSizeInMB: 50,
    allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'application/json', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'],
    allowedExtensions: ['.pdf','.doc','.docx','.txt','.md','.json','.xls', '.xlsx', '.csv'],
    maxFilenameLength: 50
  };

  convertFileToBase64(file: File): Observable<FileConversionResult> {
    return new Observable(observer => {
      const reader = new FileReader();
      
      reader.onload = (event: any) => {
        const arrayBuffer = event.target.result;
        const base64Content = this.arrayBufferToBase64(arrayBuffer);
        const base64Full = `data:${file.type};base64,${base64Content}`;
        
        const result: FileConversionResult = {
          file,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          base64Full,
          base64Content
        };
        
        observer.next(result);
        observer.complete();
      };
      
      reader.onerror = (error) => {
        observer.error(new Error(`BASEKNOWLEDGECATALOG.ERRORS.ERROR_READING_FILE`));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 8192;
    let result = '';
    
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.slice(i, i + chunkSize);
      result += String.fromCharCode.apply(null, Array.from(chunk));
    }
    
    return btoa(result);
  }

  convertFilesToBase64(files: File[]): Observable<FileConversionResult[]> {
    return new Observable(observer => {
      const results: FileConversionResult[] = [];
      let completedCount = 0;
      
      if (files.length === 0) {
        observer.next([]);
        observer.complete();
        return;
      }
      
      files.forEach(file => {
        this.convertFileToBase64(file).subscribe({
          next: (result) => {
            results.push(result);
            completedCount++;
            
            if (completedCount === files.length) {
              observer.next(results);
              observer.complete();
            }
          },
          error: (error) => {
            observer.error(error);
          }
        });
      });
    });
  }

  validateFile(file: File, config: FileValidationConfig = this.defaultValidationConfig): { isValid: boolean; error?: string } {
    // Check filename length
    if (config.maxFilenameLength && file.name.length > config.maxFilenameLength) {
      return {
        isValid: false,
        error: `BASEKNOWLEDGECATALOG.ERRORS.FILENAME_TOO_LONG|${config.maxFilenameLength}`
      };
    }

    // Check file size
    if (config.maxSizeInMB && file.size > config.maxSizeInMB * 1024 * 1024) {
      return {
        isValid: false,
        error: `BASEKNOWLEDGECATALOG.ERRORS.FILE_SIZE_EXCEEDS_LIMIT|${config.maxSizeInMB}`
      };
    }

    // Check file extension
    if (config.allowedExtensions && config.allowedExtensions.length > 0) {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const isExtensionAllowed = config.allowedExtensions.includes(fileExtension);

      if (!isExtensionAllowed) {
        return {
          isValid: false,
          error: `BASEKNOWLEDGECATALOG.ERRORS.FILE_EXTENSION_NOT_ALLOWED|${fileExtension}`
        };
      }
    }

    return { isValid: true };
  }

  validateFiles(files: File[], config: FileValidationConfig = this.defaultValidationConfig): { file: File; isValid: boolean; error?: string }[] {
    return files.map(file => ({
      file,
      ...this.validateFile(file, config)
    }));
  }

  getFileInfo(file: File): { name: string; size: number; type: string; sizeFormatted: string } {
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      sizeFormatted: this.formatFileSize(file.size)
    };
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  removeFileExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  }

  getFileExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
  }
}
